import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLessonStore } from '@/store/lessonStore'
import { apiClient } from '@/services/api'
import { webSocketService } from '@/services/websocket'
import toast from 'react-hot-toast'
import { resolvePhishingAnswer } from '@/utils/phishing'
import {
  Play,
  Pause,
  Settings,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Clock,
  Target,
  Users,
  XCircle,
  Loader2,
  SkipForward
} from 'lucide-react'
import type { LessonActivity } from '@/types'
import { audioService } from '@/services/audio'

const MULTI_QUESTION_TYPES = ['packet_analysis', 'cyber_hygiene_quiz', 'coding_quiz']

interface CompletedActivityStats {
  activityType: string
  responseCount: number
  totalPlayers: number
  correctCount: number
  incorrectCount: number
  totalQuestions: number
  avgResponseTime: number
  avgScore: number | null
  fastestResponse: number | null
  topResponders: Array<{ name: string; time: number; score: number | null }>
}

export function ActivityController() {
  const { currentLesson, currentActivity, currentSlide, setCurrentActivity, updateLessonState } = useLessonStore()
  const [isLoading, setIsLoading] = useState(false)
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null)
  const [activityStats, setActivityStats] = useState<Record<string, CompletedActivityStats>>({})
  const [loadingStats, setLoadingStats] = useState<string | null>(null)

  const toggleActivityStats = async (activity: LessonActivity) => {
    if (activity.status !== 'completed') return
    if (expandedActivityId === activity.id) {
      setExpandedActivityId(null)
      return
    }
    setExpandedActivityId(activity.id)

    // Only fetch if we haven't already
    if (activityStats[activity.id]) return

    if (!currentLesson?.id) return
    setLoadingStats(activity.id)
    try {
      const res = await apiClient.getResponses(currentLesson.id, activity.id)
      if (res.success && res.data) {
        const responses = Array.isArray(res.data) ? res.data : (res.data as any).responses || []
        const nonHostPlayers = currentLesson.players?.filter((p: any) => !p.is_host) || []
        const isMultiQ = MULTI_QUESTION_TYPES.includes(activity.activity_type)
        let correctCount = 0
        let incorrectCount = 0
        let totalQuestions = 0
        let totalTime = 0
        let timedCount = 0
        let fastest: number | null = null
        let totalScore = 0
        let scoredCount = 0
        const responders: Array<{ name: string; time: number; score: number | null }> = []

        for (const r of responses) {
          const score = r.score ?? null
          const responseTime = r.response_time != null ? Math.max(0, r.response_time) : null
          const content = r.content || {}

          if (isMultiQ && Array.isArray(content.answers)) {
            for (const ans of content.answers) {
              totalQuestions++
              if (ans.correct) correctCount++
              else incorrectCount++
            }
          } else {
            totalQuestions++
            if (score !== null) {
              if (score > 0) correctCount++
              else incorrectCount++
            }
          }

          if (score !== null) {
            totalScore += score
            scoredCount++
          }
          if (responseTime !== null) {
            totalTime += responseTime
            timedCount++
            if (fastest === null || responseTime < fastest) fastest = responseTime
          }
          responders.push({
            name: r.player?.display_name || 'Unknown',
            time: responseTime ?? Infinity,
            score,
          })
        }

        setActivityStats(prev => ({
          ...prev,
          [activity.id]: {
            activityType: activity.activity_type,
            responseCount: responses.length,
            totalPlayers: nonHostPlayers.length,
            correctCount,
            incorrectCount,
            totalQuestions,
            avgResponseTime: timedCount > 0 ? totalTime / timedCount : 0,
            avgScore: scoredCount > 0 ? totalScore / scoredCount : null,
            fastestResponse: fastest,
            topResponders: isMultiQ
              ? responders
                  .filter(r => r.score !== null)
                  .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
                  .slice(0, 3)
              : responders
                  .filter(r => r.score !== null && r.score > 0 && r.time !== Infinity)
                  .sort((a, b) => a.time - b.time)
                  .slice(0, 3),
          }
        }))
      }
    } catch {
      // Silently ignore
    } finally {
      setLoadingStats(null)
    }
  }

  // currentActivity is managed by the store via WebSocket events and host_lesson_state.
  // Do not derive it here from currentLesson.activities -- that causes a race condition
  // where a cleared activity gets re-set before the activities list is updated.

  const handleStartActivity = async (activityId: string) => {
    if (!currentLesson?.id) return

    // Prevent starting if another activity is active
    if (currentActivity && currentActivity.status === 'active') {
      toast.error(`Cannot start activity. Please complete "${currentActivity.title}" first.`)
      return
    }

    try {
      setIsLoading(true)

      // Auto-start the lesson if it hasn't been started yet
      if (currentLesson.status === 'draft' || currentLesson.status === 'ready') {
        const lessonStartResult = await apiClient.startLesson(currentLesson.id)
        if (lessonStartResult.success) {
          updateLessonState({ status: 'live' })
        } else {
          toast.error('Failed to start lesson. Please use the "Start Lesson" button first.')
          return
        }
      }

      const response = await apiClient.startActivity(currentLesson.id, activityId)
      if (response.success) {
        audioService.play('activity-start')
        // If the response contains the updated activity, set it immediately
        // This helps the timer appear faster than waiting for WebSocket
        if (response.data) {
          setCurrentActivity(response.data)
        }
      }
    } catch (error: any) {

      // Extract actual server error message if available
      const serverMessage = error.response?.data?.error?.message || error.response?.data?.message
      if (error.response?.status === 422) {
        toast.error(serverMessage || 'Cannot start activity. Please check the lesson and activity status.')
      } else {
        toast.error('Failed to start activity. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleEndActivity = async () => {
    if (!currentLesson?.id || !currentActivity?.id) return
    try {
      const response = await apiClient.skipActivity(currentLesson.id, currentActivity.id)
      if (response.success) {
        const store = useLessonStore.getState()
        store.setCurrentActivity(null)
        store.setResponses([])
        if (response.data) {
          store.updateActivityInLesson(response.data)
        }
      }
    } catch {
      toast.error('Failed to end activity. Please try again.')
    }
  }

  const handleChangeSlide = (page: number) => {
    if (!currentLesson?.id || !currentActivity?.id) return
    const totalSlides = currentActivity.activity_data?.total_slides ?? 1
    if (page < 1 || page > totalSlides) return
    // Optimistically update the slide so the UI responds immediately
    useLessonStore.getState().setCurrentSlide(page)
    // Try WebSocket first (real-time broadcast to all clients)
    webSocketService.performHostAction(currentLesson.id, 'change_slide', {
      activity_id: currentActivity.id,
      page
    })
    // Also persist via REST API as fallback
    apiClient.updateActivity(currentLesson.id, currentActivity.id, {
      activity_data: { ...currentActivity.activity_data, current_slide: page },
    } as any).catch(() => {/* best-effort */})
  }

  const getActivityIcon = (activity: LessonActivity) => {
    switch (activity.status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />
      case 'active':
        return <Play className="w-4 h-4 text-[var(--color-primary)]" />
      case 'paused':
        return <Pause className="w-4 h-4 text-yellow-400" />
      default:
        return null // Removed the Circle icon for pending activities
    }
  }

  const getActivityTypeLabel = (activityType: string) => {
    const typeMap: Record<string, string> = {
      'mcq': 'Multiple Choice',
      'coding': 'Coding Challenge',
      'pdf_slideshow': 'Slide',
      'google_slides': 'Google Slides',
      'command_shell': 'Command Shell',
      'phishing_quiz': 'Phishing Quiz',
      'packet_analysis': 'Packet Analysis',
      'cyber_hygiene_quiz': 'Cyber Hygiene Quiz',
      'coding_quiz': 'Coding Quiz',
      'python_repl': 'Python Coding',
      'youtube_video': 'YouTube Video'
    }
    return typeMap[activityType] || activityType
  }

  const getActivityTitle = (activity: LessonActivity) => {
    const data = activity.activity_data as any
    // Prefer the question/title from activity_data over the generic activity title
    return data?.question || data?.title || activity.title || `Activity ${activity.sequence_number || ''}`
  }

  if (!currentLesson || !currentLesson.activities) {
    return (
      <div className="bg-surface-deep rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-[var(--color-primary)]" />
          <h3 className="ds-section-title">Activity Control</h3>
        </div>
        <div className="text-center text-theme-text-muted py-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No activities found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-surface-deep rounded-lg p-4 overflow-hidden">
      <div className="flex items-center gap-2 mb-3">
        <Settings className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
        <h3 className="ds-section-title">Activity Control</h3>
      </div>

      {/* Current activity controls */}
      {currentActivity && (
        <div className="mb-6 p-4 bg-surface-hover rounded-lg border-l-4 border-[var(--color-primary)]">
          <div className="flex items-center gap-2 mb-3">
            {getActivityIcon(currentActivity)}
            <span className="font-medium">Current Activity</span>
            {currentActivity.status === 'active' && (
              <span className="px-2 py-1 text-xs bg-green-900 text-green-300 rounded-full">
                ACTIVE
              </span>
            )}
          </div>

          <h4 className="font-semibold mb-1 truncate">{getActivityTitle(currentActivity)}</h4>
          <div className="text-sm text-theme-text-muted mb-3 truncate">
            {getActivityTypeLabel(currentActivity.activity_type)}
            {currentActivity.response_count !== undefined && (
              <span className="ml-2">
                {currentActivity.response_count} responses
              </span>
            )}
          </div>

          {/* Instructor answer info panel */}
          {currentActivity.activity_type === 'command_shell' && (
            <div className="mb-3 p-3 bg-surface-hover rounded-lg text-sm space-y-2">
              {currentActivity.activity_data?.command && (
                <div>
                  <span className="text-theme-text-muted text-xs uppercase tracking-wide">Command</span>
                  <p className="font-mono text-[var(--color-primary)]">{currentActivity.activity_data.command}</p>
                </div>
              )}
              {currentActivity.activity_data?.correct_answer && (
                <div>
                  <span className="text-theme-text-muted text-xs uppercase tracking-wide">Expected Answer</span>
                  <p className="font-mono text-green-300">{currentActivity.activity_data.correct_answer}</p>
                </div>
              )}
              {currentActivity.activity_data?.allowed_commands?.length > 0 && (
                <div>
                  <span className="text-theme-text-muted text-xs uppercase tracking-wide">Allowed Commands</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(currentActivity.activity_data.allowed_commands as string[]).map((cmd: string) => (
                      <span key={cmd} className="px-1.5 py-0.5 bg-emerald-900 text-emerald-300 font-mono text-xs rounded">
                        {cmd}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {['mcq', 'cyber_hygiene_quiz', 'coding_quiz', 'packet_analysis'].includes(currentActivity.activity_type) && (currentActivity.activity_data as any)?.options && (currentActivity.activity_data as any)?.correct_answer != null && (
            <div className="mb-3 p-3 bg-surface-hover rounded-lg text-sm">
              <span className="text-theme-text-muted text-xs uppercase tracking-wide">Correct Answer</span>
              <p className="font-mono text-green-300 mt-1">
                {String.fromCharCode(65 + (currentActivity.activity_data as any).correct_answer)}. {(currentActivity.activity_data as any).options[(currentActivity.activity_data as any).correct_answer]}
              </p>
            </div>
          )}
          {currentActivity.activity_type === 'phishing_quiz' && resolvePhishingAnswer(currentActivity.activity_data as any) && (
            <div className="mb-3 p-3 bg-surface-hover rounded-lg text-sm">
              <span className="text-theme-text-muted text-xs uppercase tracking-wide">Answer</span>
              <p className={`font-semibold mt-1 ${
                resolvePhishingAnswer(currentActivity.activity_data as any) === 'phishing' ? 'text-red-300' : 'text-green-300'
              }`}>
                {resolvePhishingAnswer(currentActivity.activity_data as any) === 'phishing' ? 'Phishing' : 'Legitimate'}
              </p>
            </div>
          )}

          {/* End Activity button for slide types (no timer panel shown for these) */}
          {(currentActivity.activity_type === 'pdf_slideshow' || currentActivity.activity_type === 'google_slides' || currentActivity.activity_type === 'youtube_video') && (
            <button
              onClick={handleEndActivity}
              className="ds-btn ds-btn-danger ds-btn-full"
            >
              <SkipForward className="w-4 h-4" />
              End Activity
            </button>
          )}
        </div>
      )}

      {/* Activities list */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-theme-text">All Activities</h4>
          {currentActivity && currentActivity.status === 'active' && (
            <div className="text-xs text-yellow-400 bg-yellow-900/20 px-2 py-1 rounded">
              Only one activity can run at a time
            </div>
          )}
        </div>

        {currentLesson.activities.map((activity, index) => (
          <div key={activity.id}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => toggleActivityStats(activity)}
              className={`p-3 rounded-lg border transition-all ${
                currentActivity?.id === activity.id
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-subtle)]'
                  : currentActivity && currentActivity.status === 'active' && activity.status === 'pending'
                  ? 'border-theme-border bg-surface-hover/30 opacity-60'
                  : activity.status === 'completed'
                  ? 'border-theme-border bg-surface-hover/50 hover:bg-surface-hover cursor-pointer'
                  : 'border-theme-border bg-surface-hover/50 hover:bg-surface-hover'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mt-0.5 shrink-0">
                    {getActivityIcon(activity)}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{getActivityTitle(activity)}</div>
                    <div className="text-xs text-theme-text-muted truncate">
                      {getActivityTypeLabel(activity.activity_type)}
                      {activity.time_limit_seconds && !['pdf_slideshow', 'google_slides', 'youtube_video'].includes(activity.activity_type) && (
                        <span className="ml-2">
                          {Math.floor(activity.time_limit_seconds / 60)}min
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {activity.status === 'pending' && currentActivity?.id !== activity.id && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleStartActivity(activity.id) }}
                    disabled={isLoading || !!(currentActivity && currentActivity.status === 'active')}
                    className="ds-btn ds-btn-primary ds-btn-sm shrink-0"
                    title={currentActivity && currentActivity.status === 'active' ? `Cannot start while "${currentActivity.title}" is active` : 'Start this activity'}
                  >
                    <Play className="w-3 h-3" />
                    Start
                  </button>
                )}

                {activity.status === 'completed' && (
                  <ChevronDown className={`w-4 h-4 text-theme-text-muted transition-transform shrink-0 ${
                    expandedActivityId === activity.id ? 'rotate-180' : ''
                  }`} />
                )}
              </div>
            </motion.div>

            {/* Expanded analytics panel */}
            <AnimatePresence>
              {expandedActivityId === activity.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="mx-1 mt-1 p-3 bg-surface-hover/80 rounded-lg border border-theme-border space-y-3">
                    {loadingStats === activity.id ? (
                      <div className="flex items-center justify-center py-4 text-theme-text-muted gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Loading analytics...</span>
                      </div>
                    ) : activityStats[activity.id] ? (
                      (() => {
                        const stats = activityStats[activity.id]
                        const isMultiQ = MULTI_QUESTION_TYPES.includes(stats.activityType)
                        const responseRate = stats.totalPlayers > 0
                          ? Math.round((stats.responseCount / stats.totalPlayers) * 100)
                          : 0
                        const hasScoring = stats.correctCount + stats.incorrectCount > 0
                        const correctRate = stats.totalQuestions > 0
                          ? Math.round((stats.correctCount / stats.totalQuestions) * 100)
                          : 0

                        return (
                          <>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="p-2 bg-surface-deep rounded-lg">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <Users className="w-3.5 h-3.5 text-[var(--color-primary)]" />
                                  <span className="text-xs text-theme-text-muted">Response Rate</span>
                                </div>
                                <div className="text-lg font-bold text-[var(--color-primary)]">{responseRate}%</div>
                                <div className="text-xs text-theme-muted">{stats.responseCount}/{stats.totalPlayers}</div>
                              </div>

                              <div className="p-2 bg-surface-deep rounded-lg">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <Clock className="w-3.5 h-3.5 text-purple-400" />
                                  <span className="text-xs text-theme-text-muted">Avg Time</span>
                                </div>
                                <div className="text-lg font-bold text-purple-400">
                                  {Math.round(stats.avgResponseTime)}s
                                </div>
                                {stats.fastestResponse !== null && (
                                  <div className="text-xs text-theme-muted">Fastest: {Math.round(stats.fastestResponse)}s</div>
                                )}
                              </div>

                              {hasScoring && (
                                <div className="p-2 bg-surface-deep rounded-lg">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                                    <span className="text-xs text-theme-text-muted">Correct</span>
                                  </div>
                                  <div className="text-lg font-bold text-green-400">{correctRate}%</div>
                                  <div className="text-xs text-theme-muted">
                                    {stats.correctCount}/{stats.totalQuestions} {isMultiQ ? 'answers' : 'responses'}
                                  </div>
                                </div>
                              )}

                              {hasScoring && (
                                <div className="p-2 bg-surface-deep rounded-lg">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <XCircle className="w-3.5 h-3.5 text-red-400" />
                                    <span className="text-xs text-theme-text-muted">Incorrect</span>
                                  </div>
                                  <div className="text-lg font-bold text-red-400">{stats.incorrectCount}</div>
                                  <div className="text-xs text-theme-muted">
                                    out of {stats.totalQuestions} {isMultiQ ? 'answers' : 'responses'}
                                  </div>
                                </div>
                              )}
                            </div>

                            {stats.topResponders.length > 0 && (
                              <div>
                                <div className="flex items-center gap-1.5 mb-2">
                                  <Target className="w-3.5 h-3.5 text-yellow-400" />
                                  <span className="text-xs text-theme-text-muted">
                                    {isMultiQ ? 'Top Scores' : 'Fastest Correct'}
                                  </span>
                                </div>
                                <div className="space-y-1">
                                  {stats.topResponders.map((r, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm px-2 py-1 bg-surface-deep rounded">
                                      <span className="flex items-center gap-2">
                                        <span className={`font-bold text-xs ${
                                          i === 0 ? 'text-yellow-400' : i === 1 ? 'text-theme-text' : 'text-amber-600'
                                        }`}>{i + 1}</span>
                                        <span className="truncate">{r.name}</span>
                                      </span>
                                      {isMultiQ ? (
                                        <span className="font-mono text-xs text-green-400">{r.score}%</span>
                                      ) : (
                                        <span className="font-mono text-xs text-purple-400">{Math.round(r.time)}s</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )
                      })()
                    ) : (
                      <div className="text-center text-sm text-theme-text-muted py-3">No data available</div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  )
}